import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import api from '@/api';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import FloatingContact from '@/components/FloatingContact';
import SEOHead, { generateProductSchema, generateBreadcrumbSchema } from '@/components/SEOHead';
import { trackWhatsAppClick, trackPhoneClick } from '@/utils/analytics';
import { ArrowRight, MessageCircle, Phone, ChevronRight } from 'lucide-react';

const ProductPage = () => {
  const { slug } = useParams();
  const { lang, getLocalized, getLocalizedPath } = useLanguage();
  const [product, setProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxImg, setLightboxImg] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productRes, productsRes, settingsRes, catalogRes] = await Promise.all([
          api.get(`/products/${slug}`),
          api.get('/products'),
          api.get('/site-settings'),
          api.get('/catalog')
        ]);
        setProduct(productRes.data);
        setProducts(productsRes.data);
        setSettings(settingsRes.data);
        setCatalog(catalogRes.data);
      } catch (err) {
        console.error('Error loading product:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    window.scrollTo(0, 0);
  }, [slug]);

  const getCatalogUrl = () => {
    const pdfUrl = catalog?.pdf_url;
    if (pdfUrl) {
      if (pdfUrl.startsWith('http')) return pdfUrl;
      return `${process.env.REACT_APP_BACKEND_URL}${pdfUrl}`;
    }
    return '/Blackline_PS_Product_Catalog_2026.pdf';
  };

  if (loading) return <div style={{ minHeight: '100vh', background: 'var(--black)' }} />;
  if (!product) return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><h2>Product not found</h2></div>;

  const loc = getLocalized(product);
  const contact = settings?.contact || {};
  const relatedProducts = products.filter(p => p.slug !== slug).slice(0, 3);

  const cleanText = (text) => {
    if (!text) return text;
    return text.replace(/aluminum\s*/gi, '').replace(/aluminio\s*/gi, '').replace(/\s{2,}/g, ' ').trim();
  };

  return (
    <div data-testid="product-page">
      <SEOHead
        title={loc.name}
        description={loc.description || loc.one_liner}
        path={`/products/${product.slug}`}
        image={product.image_url}
        schema={generateProductSchema(product, lang)}
      />
      <Navbar />

      {/* Lightbox */}
      {lightboxImg && (
        <div className="bl-lightbox" onClick={() => setLightboxImg(null)} data-testid="lightbox">
          <button className="bl-lightbox-close" onClick={() => setLightboxImg(null)}>&times;</button>
          <img src={lightboxImg} alt="" />
        </div>
      )}

      {/* 1. HERO */}
      <section className="bl-subpage-hero" data-testid="product-hero">
        <div className="bl-subpage-hero-bg" style={{ backgroundImage: `url(${product.image_url})` }} />
        <div className="bl-subpage-hero-content">
          <div className="eyebrow">{lang === 'es' ? 'NUESTROS PRODUCTOS' : 'OUR PRODUCTS'}</div>
          <h1>{loc.name}</h1>
          <p>{cleanText(loc.description)}</p>
          <nav className="bl-breadcrumb" data-testid="breadcrumb">
            <Link to={getLocalizedPath('/')}>{lang === 'es' ? 'Inicio' : 'Home'}</Link>
            <ChevronRight size={14} />
            <Link to={getLocalizedPath('/')} onClick={(e) => { e.preventDefault(); window.location.href = getLocalizedPath('/') + '#products'; }}>{lang === 'es' ? 'Productos' : 'Products'}</Link>
            <ChevronRight size={14} />
            <span>{loc.name}</span>
          </nav>
        </div>
      </section>

      {/* 2. INTRODUCTION (50/50) */}
      {loc.intro_title && (
        <section className="bl-product-intro" data-testid="product-intro">
          <div className="bl-product-intro-text">
            <div className="bl-section-eyebrow">{loc.intro_eyebrow}</div>
            <h2 className="bl-section-title">{loc.intro_title}</h2>
            <p>{cleanText(loc.intro_body)}</p>
          </div>
          <div className="bl-product-intro-img">
            <img src={product.gallery?.[1] || product.image_url} alt={loc.name} loading="lazy" />
          </div>
        </section>
      )}

      {/* 3. KEY FEATURES (2x2 grid) */}
      {loc.features && loc.features.length > 0 && (
        <section className="bl-product-features-grid" data-testid="product-features">
          {loc.features.map((feature, i) => (
            <div className="bl-feature-card" key={i}>
              <span className="bl-feature-num">{feature.num}</span>
              <h3>{feature.title}</h3>
              <p>{cleanText(feature.desc)}</p>
            </div>
          ))}
        </section>
      )}

      {/* 4. GALLERY (scrollable with lightbox) */}
      {product.gallery && product.gallery.length > 0 && (
        <section className="bl-product-gallery" data-testid="product-gallery">
          <div className="bl-product-gallery-scroll">
            {product.gallery.map((img, i) => (
              <div className="bl-product-gallery-item" key={i} onClick={() => setLightboxImg(img)}>
                <img src={img} alt={`${loc.name} - ${i + 1}`} loading="lazy" />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 5. TECHNICAL SPECIFICATIONS - REMOVED (custom products, no limits) */}

      {/* 6. CUSTOMIZATION OPTIONS */}
      {loc.customization && loc.customization.length > 0 && (
        <section className="bl-product-custom" data-testid="product-customization">
          <div className="bl-section-eyebrow">{lang === 'es' ? 'Personalizaci\u00f3n' : 'Customization'}</div>
          <h2 className="bl-section-title">{lang === 'es' ? 'Opciones Disponibles' : 'Available Options'}</h2>
          <div className="bl-custom-grid">
            {loc.customization.map((opt, i) => (
              <div className="bl-custom-item" key={i}>
                <h4>{opt.category}</h4>
                <p>{opt.options}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 7. PRODUCT CTA */}
      <section className="bl-product-cta" data-testid="product-cta">
        <div className="bl-product-cta-inner">
          <div className="bl-product-cta-text">
            <h2>{loc.cta_title || `${lang === 'es' ? 'Interesado en' : 'Interested in'} ${loc.name}?`}</h2>
            <p>{loc.cta_desc || ''}</p>
          </div>
          <div className="bl-product-cta-buttons">
            <Link
              to={getLocalizedPath('/')}
              className="btn-gold"
              onClick={() => setTimeout(() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }), 300)}
              data-testid="product-quote-btn"
            >
              {loc.cta_primary || `${lang === 'es' ? 'Solicitar Cotización' : 'Request a Quote'}`}
            </Link>
            <a href={getCatalogUrl()} className="btn-ghost" target="_blank" rel="noopener noreferrer" data-testid="product-specs-btn">
              {loc.cta_secondary || `${lang === 'es' ? 'Descargar Catálogo' : 'Download Catalog'}`}
            </a>
          </div>
        </div>
      </section>

      {/* 8. RELATED PRODUCTS */}
      {relatedProducts.length > 0 && (
        <section className="bl-related-products" data-testid="related-products">
          <div className="bl-section-eyebrow">{lang === 'es' ? 'M\u00e1s Productos' : 'More Products'}</div>
          <h2 className="bl-section-title">{lang === 'es' ? 'Explorar M\u00e1s Productos' : 'Explore More Products'}</h2>
          <div className="bl-related-grid">
            {relatedProducts.map((p) => {
              const pLoc = getLocalized(p);
              return (
                <Link to={getLocalizedPath(`/products/${p.slug}`)} className="bl-product-card" key={p.slug}>
                  <img src={p.image_url} alt={pLoc.name} className="bl-product-card-img" loading="lazy" />
                  <div className="bl-product-card-body">
                    <div className="bl-product-card-name">{pLoc.name}</div>
                    <div className="bl-product-card-sub">{pLoc.one_liner}</div>
                    <div className="bl-product-card-link">{lang === 'es' ? 'Explorar' : 'Explore'} <ArrowRight size={12} /></div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      <Footer products={products} settings={settings} />
      <FloatingContact settings={settings} />
    </div>
  );
};

export default ProductPage;
